import type { ReportColumn } from '../models/column.types'
import type { ReportTableType } from '../models/tablePresentation.types'

export interface MatrixPresentationOptions {
  breakdownField: string | null | undefined
  matrixDimension?: string | null
  pivotMetric?: string | null
}

export interface MatrixPivotAssignment {
  breakdownField: string
  matrixDimension: string
  pivotMetric: string
}

export interface ReportTableAvailabilityContext extends MatrixPresentationOptions {
  columns: readonly ReportColumn[]
}

/** Resolve the whole visible pivot tuple at once; explicit invalid fields never silently retarget. */
export function resolveMatrixPivotAssignment(
  columns: readonly ReportColumn[],
  options: MatrixPresentationOptions,
): MatrixPivotAssignment | null {
  const dimensions = columns.filter(
    (column) => !column.invisible && !column.numeric && !column.isImage,
  )
  const metrics = columns.filter((column) => !column.invisible && column.numeric)
  if (!options.breakdownField) return null
  const breakdown = dimensions.find((column) => column.field === options.breakdownField)
  if (!breakdown) return null

  const matrixDimension = options.matrixDimension
    ? dimensions.find((column) => column.field === options.matrixDimension)
    : dimensions.find((column) => column.field !== breakdown.field)
  if (!matrixDimension || matrixDimension.field === breakdown.field) return null

  const pivotMetric = options.pivotMetric
    ? metrics.find((column) => column.field === options.pivotMetric)
    : metrics[0]
  if (!pivotMetric) return null
  return {
    breakdownField: breakdown.field,
    matrixDimension: matrixDimension.field,
    pivotMetric: pivotMetric.field,
  }
}

export interface TrendPresentationOptions {
  breakdownField: string | null | undefined
}

export interface TrendAssignment {
  breakdownField: string
  previewMetric: string
  dateDimensions: ReportColumn[]
}

function isTrendDateColumn(column: ReportColumn): boolean {
  return (
    !column.invisible &&
    !column.numeric &&
    !column.isImage &&
    (column.date_col === true || column.sub_type === 'date' || column.sub_type === 'datetime')
  )
}

/** Trend's counterpart to `resolveMatrixPivotAssignment`: the breakdown is user-chosen (the same
 * field the "Breakdown" select already drives), while the date bucket columns and preview metric
 * auto-resolve from whichever columns are visible — no separate "Dimension"/"Metric" selectors. */
export function resolveTrendAssignment(
  columns: readonly ReportColumn[],
  options: TrendPresentationOptions,
): TrendAssignment | null {
  if (!options.breakdownField) return null
  const dimensions = columns.filter(
    (column) => !column.invisible && !column.numeric && !column.isImage,
  )
  const breakdown = dimensions.find((column) => column.field === options.breakdownField)
  if (!breakdown) return null

  const dateDimensions = columns.filter(isTrendDateColumn)
  if (dateDimensions.length === 0) return null

  const previewMetric = columns.find((column) => !column.invisible && column.numeric)
  if (!previewMetric) return null

  return { breakdownField: breakdown.field, previewMetric: previewMetric.field, dateDimensions }
}

/** Pivot-shaped types resolve their own visible-column assignment before they can be offered;
 * every other type has no such precondition. */
export function isPivotTableTypeAvailable(
  type: ReportTableType,
  context: ReportTableAvailabilityContext | undefined,
): boolean {
  if (type !== 'matrix_table' && type !== 'trend_table') return true
  if (!context) return false
  const assignment =
    type === 'matrix_table'
      ? resolveMatrixPivotAssignment(context.columns, context)
      : resolveTrendAssignment(context.columns, context)
  return assignment !== null
}

const NON_ADDITIVE_AGGREGATION =
  /(?:^|_)(?:average|avg|mean|median|rate|ratio|percent|percentage|min|max|distinct|unique)(?:$|_)/u
const ADDITIVE_AGGREGATION = /(?:^|_)(?:sum|total|count)(?:$|_)/u

function aggregationMetadata(column: ReportColumn): unknown[] {
  const meta = column.meta ?? {}
  return [
    column.functionType,
    meta['aggregation'],
    meta['aggregationType'],
    meta['aggregation_type'],
    meta['aggregate'],
    meta['metricType'],
    meta['metric_type'],
    column.field,
  ]
}

/** Synthetic parents/pivots may sum only metrics whose column contract is additive. */
export function isAdditiveReportMetric(column: ReportColumn): boolean {
  if (!column.numeric || column.percentage || column.sub_type === 'percentage') return false
  const explicit =
    column.meta?.['additive'] ?? column.meta?.['isAdditive'] ?? column.meta?.['is_additive']
  if (typeof explicit === 'boolean') return explicit
  for (const value of aggregationMetadata(column)) {
    if (typeof value !== 'string') continue
    const normalized = value
      .trim()
      .toLowerCase()
      .replaceAll(/[^a-z0-9]+/gu, '_')
    if (NON_ADDITIVE_AGGREGATION.test(normalized)) return false
    if (ADDITIVE_AGGREGATION.test(normalized)) return true
  }
  return true
}
